import type { OrderJourneyQueryResponse } from '../graphql/queries/orderJourney.generated';
import type { EJourneyEvent } from '~/presentation/journey/ui/type';
import type { InfiniteData } from '@tanstack/react-query';
import type { OrderJourneyInput } from '~/types/graphql';
import type { ModalType } from '~/presentation/ui';
import type { IOrderJourneyItem } from '../types';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useGModalContext } from '~/presentation/ui';
import { useTranslation } from 'react-i18next';
import { fetchOrderJourney } from '../helpers';
import { useToast } from '~/hooks/useToast';

const convertFn = (response?: InfiniteData<OrderJourneyQueryResponse>): IOrderJourneyItem[] => {
  return (
    response?.pages?.flatMap(
      (page) =>
        page?.orderJourney?.journeys?.map((journey) => ({
          ...journey,
          items: journey?.items?.map((item) => item ?? null) ?? null,
          lineItems: journey?.items?.filter((item) => !!item) ?? [],
          date: journey?.date ?? '',
          name: journey?.name ?? '',
          orderID: journey?.orderID ?? '',
          eventType: journey?.name as EJourneyEvent,
        })) ?? [],
    ) ?? []
  );
};

export const useQueryOrderJourney = ({ orderID }: Pick<OrderJourneyInput, 'orderID'>) => {
  const { t } = useTranslation();
  const { shopifyToast } = useToast();

  const { onCloseModal } = useGModalContext<ModalType.ORDER_JOURNEY>();

  const {
    data,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery<OrderJourneyQueryResponse, Error>({
    queryKey: getOrderJourneyQueryKey({ orderID }),
    queryFn: ({ pageParam = '' }) => fetchOrderJourney({ input: { orderID, cursor: pageParam } }),
    enabled: !!orderID,
    retry: false,
    select: (data) => ({
      pages: [...data.pages].reverse(),
      pageParams: [...data.pageParams].reverse(),
    }),
    getNextPageParam: (lastPage) => {
      const cursor = lastPage?.orderJourney?.cursor;
      return cursor ? cursor : undefined;
    },
    onError: () => {
      onCloseModal();
      shopifyToast(t('Order journey not found'), { isError: true });
    },
  });

  return {
    data: convertFn(data),
    hasNextPage,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
  };
};

export function getOrderJourneyQueryKey(orderID: Pick<OrderJourneyInput, 'orderID'>) {
  return ['orderJourney', orderID];
}
